/*
 * <prism-sticky-atc> — a sticky add-to-cart bar that appears once the main
 * add-to-cart button scrolls out of view. It drives the REAL product form
 * (so variant, quantity, and validation are respected) by clicking the main
 * submit button. Price/title mirror the selected variant via Dawn's pubsub.
 *
 * Attributes:
 *   data-target   id of the main submit button (ProductSubmitButton-<sectionId>)
 *   data-section  section id
 */
if (!customElements.get('prism-sticky-atc')) {
  customElements.define(
    'prism-sticky-atc',
    class PrismStickyAtc extends HTMLElement {
      connectedCallback() {
        this.targetButton = document.getElementById(this.getAttribute('data-target'));
        this.sectionId = this.getAttribute('data-section');
        this.addButton = this.querySelector('.prism-sticky-atc__button');
        this.priceEl = this.querySelector('.prism-sticky-atc__price');

        if (!this.targetButton) {
          this.remove();
          return;
        }

        this.onAdd = this.onAdd.bind(this);
        this.addButton.addEventListener('click', this.onAdd);

        // Show the bar only when the real ATC is out of view (and below the fold).
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const past = entry.boundingClientRect.top < 0; // scrolled past, not above-fold
              this.classList.toggle('prism-sticky-atc--visible', !entry.isIntersecting && past);
            });
          },
          { rootMargin: '0px 0px 0px 0px', threshold: 0 }
        );
        this.observer.observe(this.targetButton);

        // Mirror variant price/availability changes.
        if (window.PUB_SUB_EVENTS && typeof subscribe === 'function') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (!event || !event.data || event.data.sectionId !== this.sectionId) return;
            const variant = event.data.variant;
            if (!variant) {
              this.setUnavailable();
              return;
            }
            this.setAvailable(variant);
          });
        }
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.unsubscribe) this.unsubscribe();
        if (this.addButton) this.addButton.removeEventListener('click', this.onAdd);
      }

      onAdd(e) {
        e.preventDefault();
        // Defer to the real form: respects selected variant, quantity & inline errors.
        this.targetButton.click();
      }

      setUnavailable() {
        this.addButton.setAttribute('disabled', 'disabled');
      }

      setAvailable(variant) {
        if (variant.available) this.addButton.removeAttribute('disabled');
        else this.addButton.setAttribute('disabled', 'disabled');
        if (this.priceEl && typeof variant.price === 'number') {
          const fmt = (document.body.getAttribute('data-prism-money-format')) || '${{amount}}';
          this.priceEl.textContent = fmt.replace(/\{\{\s*amount\s*\}\}/, (variant.price / 100).toFixed(2));
        }
      }
    }
  );
}
